<template>
  <div class="widget-carousel-products-list">
    <div
        v-if="!products.length"
        class="widget-carousel-products-list__empty"
    >
      {{ $t("superUpLang.widget.carousel.productsList.empty", { item: itemLabel.toLowerCase() }) }}
    </div>

    <div
        v-for="product in products"
        :key="product.local_id"
        class="widget-carousel-products-list__item"
        :class="{
        'widget-carousel-products-list__item--selected': selectedProductId === product.local_id,
        'widget-carousel-products-list__item--inactive': !product.is_active
      }"
    >
      <div
          class="widget-carousel-products-list__summary"
          @click="toggleSelection(product.local_id)"
      >
        <div class="widget-carousel-products-list__summary-left">
          <div class="widget-carousel-products-list__thumb">
            <img
                v-if="product.image && product.image.preview"
                :src="product.image.preview"
                :alt="$t('superUpLang.widget.carousel.productsList.alt.preview')"
            >
            <div v-else class="widget-carousel-products-list__thumb-placeholder">
              {{ itemLabel }}
            </div>
          </div>

          <div class="widget-carousel-products-list__meta">
            <div class="widget-carousel-products-list__title-row">
              <strong class="widget-carousel-products-list__title">
                {{ product.product_name || $t('superUpLang.widget.carousel.productsList.unnamedItem', { item: itemLabel }) }}
              </strong>

              <span
                  v-if="!product.is_active"
                  class="badge badge-danger widget-carousel-products-list__status-badge"
              >
                {{ $t("superUpLang.widget.carousel.common.inactive") }}
              </span>
            </div>

            <div
                v-if="product.brand_name"
                class="widget-carousel-products-list__subtitle"
            >
              {{ product.brand_name }}
            </div>

            <div
                v-if="!product.is_active && product.reason"
                class="widget-carousel-products-list__reason"
            >
              {{ $t("superUpLang.widget.carousel.productsList.reason", { reason: product.reason }) }}
            </div>
          </div>
        </div>

        <div class="widget-carousel-products-list__summary-right">
          <button
              type="button"
              class="btn btn-outline-secondary btn-sm mr-2"
              :disabled="isDisabled || !product.is_active"
              @click.stop="$emit('replace-product', product.local_id)"
          >
            {{ $t("superUpLang.widget.carousel.productsList.actions.replace", { item: itemLabel.toLowerCase() }) }}
          </button>

          <button
              type="button"
              class="btn btn-outline-primary btn-sm mr-2"
              :disabled="isDisabled || !product.is_active"
              @click.stop="duplicateProduct(product.local_id)"
          >
            {{ $t("superUpLang.widget.carousel.productsList.actions.duplicate") }}
          </button>

          <button
              type="button"
              class="btn btn-outline-danger btn-sm"
              :disabled="isDisabled || !product.is_active"
              @click.stop="removeProduct(product.local_id)"
          >
            {{ $t("superUpLang.widget.carousel.productsList.actions.delete") }}
          </button>
        </div>
      </div>

      <div
          v-if="selectedProductId === product.local_id"
          class="widget-carousel-products-list__editor"
      >
        <WidgetCarouselProductEditor
            :product="product"
            :is-disabled="isDisabled || !product.is_active"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import WidgetCarouselProductEditor from "./WidgetCarouselProductEditor.vue";

const STORE_NAMESPACE = "widgetCarousel";

export default {
  name: "WidgetCarouselProductsList",

  components: {
    WidgetCarouselProductEditor
  },

  props: {
    isDisabled: {
      type: Boolean,
      default: false
    }
  },

  computed: {
    ...mapGetters(STORE_NAMESPACE, [
      "products",
      "selectedProductId",
      "form"
    ]),

    isOffersMode() {
      return this.form && this.form.type === "offers";
    },

    itemLabel() {
      return this.isOffersMode
        ? this.$t("superUpLang.widget.carousel.common.offer")
        : this.$t("superUpLang.widget.carousel.common.product");
    }
  },

  methods: {
    toggleSelection(localId) {
      this.$store.dispatch(`${STORE_NAMESPACE}/toggleProductSelection`, localId);
    },

    removeProduct(localId) {
      this.$store.dispatch(`${STORE_NAMESPACE}/removeProduct`, localId);
    },

    duplicateProduct(localId) {
      this.$store.dispatch(`${STORE_NAMESPACE}/duplicateProduct`, localId);
    }
  }
};
</script>

<style scoped>
.widget-carousel-products-list__empty {
  padding: 18px;
  border: 1px dashed #d7dbe0;
  border-radius: 8px;
  color: #6c757d;
  text-align: center;
  background: #fafbfc;
}

.widget-carousel-products-list__item {
  border: 1px solid #e8ebef;
  border-radius: 10px;
  background: #fff;
  margin-bottom: 12px;
  overflow: hidden;
}

.widget-carousel-products-list__item--selected {
  border-color: #9d7cff;
  box-shadow: 0 0 0 1px rgba(157, 124, 255, 0.15);
}

.widget-carousel-products-list__item--inactive {
  background: #fff8f8;
}

.widget-carousel-products-list__summary {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 14px;
  cursor: pointer;
}

.widget-carousel-products-list__summary-left {
  display: flex;
  gap: 12px;
  min-width: 0;
}

.widget-carousel-products-list__thumb {
  width: 72px;
  height: 72px;
  border-radius: 8px;
  overflow: hidden;
  background: #f5f6f8;
  border: 1px solid #eceff3;
  flex-shrink: 0;
}

.widget-carousel-products-list__thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.widget-carousel-products-list__thumb-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 6px;
  font-size: 11px;
  text-align: center;
  color: #7b8188;
}

.widget-carousel-products-list__meta {
  min-width: 0;
}

.widget-carousel-products-list__title-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.widget-carousel-products-list__title {
  font-size: 14px;
  line-height: 1.4;
  word-break: break-word;
}

.widget-carousel-products-list__subtitle {
  font-size: 12px;
  color: #6c757d;
  margin-top: 2px;
}

.widget-carousel-products-list__reason {
  margin-top: 6px;
  font-size: 12px;
  color: #b02a37;
  font-weight: 600;
}

.widget-carousel-products-list__status-badge {
  font-size: 10px;
}

.widget-carousel-products-list__summary-right {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
}

.widget-carousel-products-list__editor {
  border-top: 1px solid #eceff3;
  padding: 14px;
  background: #fcfcfd;
}

@media (max-width: 767.98px) {
  .widget-carousel-products-list__summary {
    flex-direction: column;
  }

  .widget-carousel-products-list__summary-right {
    width: 100%;
    justify-content: flex-start;
  }
}
</style>